'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import LazyImage from '@/components/LazyImage';
import { formatKodIMO, courseSlug } from '@/lib/utils';
import { FaIdCard } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useLoading } from '@/contexts/LoadingContext';
import { useRouter } from 'next/navigation';

// Interfejs dla kursu (płaska struktura z MySQL)
export interface Course {
  id: number;
  tytul: string;
  kodimoopis?: string;
  programszkolenia?: string;
  kodimo?: number;
  czastrwania?: string;
  cena?: string;
  level?: string;
  motorowodne?: number;
  uzyskaneuprawnienia?: string;
  obraz_kursu?: string;
}

function getImageUrl(course: Course): string {
  if (course?.obraz_kursu && course.obraz_kursu !== '') {
    return `/images/${course.obraz_kursu}`;
  }
  return '/images/course-default.webp';
}

// Filtrowanie + sortowanie kursów — czysta funkcja (używana też dla stanu początkowego,
// dzięki czemu kafelki renderują się już w SSR HTML, a nie dopiero po hydratacji).
function applyFiltersAndSort(
  courses: Course[],
  activeFilter: string,
  searchQuery: string
): Course[] {
  if (courses.length === 0) return [];
  let filtered = [...courses];

  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    filtered = filtered.filter((course) => {
      const titleMatch = course.tytul.toLowerCase().includes(query);
      const kodimoMatch = course.kodimo?.toString().includes(query);
      const levelMatch = course.level?.toLowerCase().includes(query);
      const opisMatch = course.kodimoopis?.toLowerCase().includes(query);
      return titleMatch || kodimoMatch || levelMatch || opisMatch;
    });
  }

  if (activeFilter !== 'all') {
    if (activeFilter === 'motorowodne') {
      filtered = filtered.filter(
        (course) => course.motorowodne != null && course.motorowodne > 0
      );
    } else if (activeFilter === 'radiowe') {
      filtered = filtered.filter(
        (course) => course.level && (course.level.includes('Radiowe') || course.level.includes('radiowe'))
      );
    } else if (activeFilter === 'Szkolenie pełne') {
      filtered = filtered.filter(
        (course) =>
          course.level &&
          (course.level === 'Szkolenie pełne' ||
            course.level === 'Szkolenie pełne - Radiowe' ||
            course.level === 'Szkolenie pełne - Specjalistyczne')
      );
    } else if (activeFilter === 'bezpieczenstwo') {
      filtered = filtered.filter((course) => {
        const levelLower = course.level?.toLowerCase() || '';
        return levelLower.includes('bezpieczenstwo i ochrona') || course.tytul.toLowerCase().includes('stcw');
      });
    } else if (activeFilter === 'specjalistyczne') {
      filtered = filtered.filter((course) => (course.level?.toLowerCase() || '').includes('specjalistyczne'));
    } else {
      filtered = filtered.filter((course) => course.level === activeFilter);
    }
  }

  filtered.sort((a, b) => {
    if (a.motorowodne && !b.motorowodne) return 1;
    if (!a.motorowodne && b.motorowodne) return -1;

    const getPriority = (course: Course) => {
      if (course.motorowodne) return 5;
      if (course.level?.includes('Specjalistyczne')) return 4;
      if (course.level?.includes('Uaktualnienie')) return 3;
      if (course.level?.includes('Radiowe')) return 2;
      if (course.level?.includes('Bezpieczeństwo')) return 1;
      return 0;
    };

    const priorityA = getPriority(a);
    const priorityB = getPriority(b);
    if (priorityA !== priorityB) return priorityA - priorityB;
    if (a.kodimo && b.kodimo) return (a.kodimo || 0) - (b.kodimo || 0);
    return a.tytul.localeCompare(b.tytul);
  });

  return filtered;
}

export default function TrainingCatalog({
  initialCourses,
  initialSearch = '',
}: {
  initialCourses: Course[];
  initialSearch?: string;
}) {
  const [courses] = useState<Course[]>(initialCourses);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>(() =>
    applyFiltersAndSort(initialCourses, 'all', initialSearch)
  );
  const [activeFilter, setActiveFilter] = useState<string>('all');
  // Inicjalizacja z parametru ?search= (Sitelinks Searchbox / SearchAction w WebsiteSchema).
  const [searchQuery, setSearchQuery] = useState<string>(initialSearch);
  const [bannerImage, setBannerImage] = useState<string>('/images/front01.webp');

  const { stopLoading, setLoadingWithDelay, showLoadingScreen } = useLoading();
  const router = useRouter();

  // Losowy baner przy wejściu (tylko klient — uniknięcie niezgodności hydratacji)
  useEffect(() => {
    const bannerImages = ['/images/front01.webp', '/images/front02.webp', '/images/front03.webp'];
    setBannerImage(bannerImages[Math.floor(Math.random() * bannerImages.length)]);
  }, []);

  // Reset ekranu ładowania
  useEffect(() => {
    const resetTimer = setTimeout(() => {
      stopLoading();
      setLoadingWithDelay(false, 0);
      showLoadingScreen(false);
    }, 200);
    return () => clearTimeout(resetTimer);
  }, [stopLoading, setLoadingWithDelay, showLoadingScreen]);

  // Nawigacja wstecz przeglądarki
  useEffect(() => {
    const handlePopState = () => {
      stopLoading();
      setLoadingWithDelay(false, 0);
      showLoadingScreen(false);
      sessionStorage.removeItem('navigatingFromEvent');
      sessionStorage.removeItem('navigatingTime');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [stopLoading, setLoadingWithDelay, showLoadingScreen]);

  // Bezpośrednia nawigacja z podstrony wydarzenia
  useEffect(() => {
    try {
      if (sessionStorage.getItem('directTrainingNavigation') === 'true') {
        sessionStorage.removeItem('navigatingFromEvent');
        sessionStorage.removeItem('navigatingTime');
        sessionStorage.removeItem('directTrainingNavigation');
        stopLoading();
        setLoadingWithDelay(false, 0);
        showLoadingScreen(false);
      }
    } catch {
      stopLoading();
      setLoadingWithDelay(false, 0);
      showLoadingScreen(false);
    }
  }, [stopLoading, setLoadingWithDelay, showLoadingScreen]);

  // Filtrowanie reaguje na zmianę filtra/wyszukiwania
  useEffect(() => {
    setFilteredCourses(applyFiltersAndSort(courses, activeFilter, searchQuery));
  }, [activeFilter, courses, searchQuery]);

  const handleFilterChange = (filter: string) => setActiveFilter(filter);
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value);

  return (
    <div className="min-h-screen bg-blue-50">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-black">
          <div className="w-full h-full">
            <Image
              src={bannerImage}
              alt="Szkolenia morskie w Ośrodku Szkolenia Morskiego LIBRA w Kołobrzegu"
              fill
              priority
              quality={85}
              className="object-cover brightness-50"
              sizes="100vw"
            />
          </div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Katalog szkoleń morskich
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto"
          >
            Zapoznaj się z ofertą i zaplanuj swoją morską karierę.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Katalog szkoleń</h2>

          {/* Pole wyszukiwania */}
          <div className="mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Szukaj po kodzie IMO lub tytule szkolenia..."
                value={searchQuery}
                onChange={handleSearch}
                aria-label="Szukaj szkolenia"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[rgb(6,40,95)] focus:border-transparent transition-all duration-200"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Filtry */}
          <div className="mb-8 flex flex-wrap gap-3 justify-start">
            <button onClick={() => handleFilterChange('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeFilter === 'all' ? 'bg-[rgb(6,40,95)] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
              Wszystkie
            </button>
            <button onClick={() => handleFilterChange('Szkolenie pełne')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeFilter === 'Szkolenie pełne' ? 'bg-[rgb(6,40,95)] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
              Szkolenie pełne
            </button>
            <button onClick={() => handleFilterChange('Uaktualnienie certyfikatu')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeFilter === 'Uaktualnienie certyfikatu' ? 'bg-orange-500 text-gray-900' : 'bg-gray-200 text-gray-700 hover:bg-orange-100'}`}>
              Uaktualnienie certyfikatu
            </button>
            <button onClick={() => handleFilterChange('Szkolenie pełne - Specjalistyczne')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeFilter === 'Szkolenie pełne - Specjalistyczne' ? 'bg-yellow-600 text-gray-900' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
              Specjalistyczne
            </button>
            <button onClick={() => handleFilterChange('motorowodne')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeFilter === 'motorowodne' ? 'bg-[#0D59D3] text-white' : 'bg-gray-200 text-gray-700 hover:bg-[#0D59D3]/10'}`}>
              Motorowodne
            </button>
            <button onClick={() => handleFilterChange('radiowe')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeFilter === 'radiowe' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-purple-100'}`}>
              Radiowe
            </button>
            <button onClick={() => handleFilterChange('Uzupełniający')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeFilter === 'Uzupełniający' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-200 text-gray-700 hover:bg-yellow-100'}`}>
              Uzupełniający
            </button>
            <button onClick={() => handleFilterChange('bezpieczenstwo')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeFilter === 'bezpieczenstwo' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
              Bezpieczeństwo i ochrona
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map((course, index) => {
              const displayLevel = course.level && course.level.includes(' - ')
                ? course.level.split(' - ')[0].trim()
                : course.level;

              return (
                <Link
                  href={`/training/${courseSlug(course.id, course.tytul)}${course.level ? `?level=${encodeURIComponent(course.level)}` : ''}`}
                  key={course.id}
                  className="block"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className={`bg-white rounded-lg shadow-md overflow-hidden flex flex-col group relative h-full
                               transition-all duration-300 ease-in-out
                               ${course.level && (course.level.includes('Radiowe') || course.level.includes('radiowe'))
                                  ? 'hover:shadow-[0px_0px_61px_0px_rgba(147,51,234,0.9)]'
                                  : course.level && course.level === 'Szkolenie pełne - Specjalistyczne'
                                  ? 'hover:shadow-[0px_0px_61px_0px_rgba(202,138,4,0.9)]'
                                  : course.level && course.level === 'Uaktualnienie certyfikatu'
                                  ? 'hover:shadow-[0px_0px_61px_0px_rgba(249,115,22,0.9)]'
                                  : course.level && course.level === 'Uzupełniający'
                                  ? 'hover:shadow-[0px_0px_61px_0px_rgba(234,179,8,0.9)]'
                                  : course.level && course.level.startsWith('Szkolenie pełne')
                                  ? 'hover:shadow-[0px_0px_61px_0px_rgba(6,40,95,0.9)]'
                                  : course.motorowodne != null && course.motorowodne > 0
                                  ? 'hover:shadow-[0px_0px_61px_0px_rgba(13,89,211,0.9)]'
                                  : 'hover:shadow-[0px_0px_61px_0px_rgba(6,40,95,0.9)]'
                               }`}
                  >
                    <div className="relative h-48 w-full">
                      <LazyImage
                        src={getImageUrl(course)}
                        alt={course.tytul}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="object-cover"
                        priority={index < 3}
                        quality={60}
                      />
                      {course.level && (course.level.includes('Radiowe') || course.level.includes('radiowe')) ? (
                        <div className="absolute top-4 right-4 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">Radiowe</div>
                      ) : course.level && course.level === 'Szkolenie pełne - Specjalistyczne' ? (
                        <div className="absolute top-4 right-4 bg-yellow-600 text-gray-900 px-3 py-1 rounded-full text-sm font-medium">Szkolenie pełne</div>
                      ) : course.level && course.level === 'Uaktualnienie certyfikatu' ? (
                        <div className="absolute top-4 right-4 bg-orange-500 text-gray-900 px-3 py-1 rounded-full text-sm font-medium">{displayLevel}</div>
                      ) : course.level && course.level === 'Uzupełniający' ? (
                        <div className="absolute top-4 right-4 bg-yellow-500 text-gray-900 px-3 py-1 rounded-full text-sm font-medium">{displayLevel}</div>
                      ) : course.level && course.level.startsWith('Szkolenie pełne') ? (
                        <div className="absolute top-4 right-4 bg-[rgb(6,40,95)] text-white px-3 py-1 rounded-full text-sm font-medium">{displayLevel}</div>
                      ) : course.motorowodne != null && course.motorowodne > 0 ? (
                        <div className="absolute top-4 right-4 bg-[#0D59D3] text-white px-3 py-1 rounded-full text-sm font-medium">Motorowodne</div>
                      ) : null}
                    </div>

                    <div className="p-6 flex flex-col flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">{course.tytul}</h3>

                      {course.programszkolenia && (
                        <p className="text-gray-600 mb-4 line-clamp-3">{course.programszkolenia}</p>
                      )}

                      <div className="flex-grow"></div>

                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <span className="text-[rgb(6,40,95)] font-medium group-hover:underline">Zobacz szczegóły</span>
                        {course.kodimo && !course.kodimo.toString().replace(/\./g, '').match(/^0+$/) && (
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <FaIdCard className="mr-1 text-[rgb(6,40,95)]" />
                            <span>Kod IMO: {formatKodIMO(course.kodimo)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </Link>
              );
            })}
          </div>

          {filteredCourses.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <p className="text-gray-600">Nie znaleziono kursów spełniających kryteria.</p>
              <button onClick={() => setActiveFilter('all')} className="mt-4 text-[rgb(6,40,95)] underline hover:text-[rgb(6,40,95)]/80">
                Pokaż wszystkie kursy
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
